import type { CalcPlanListData, ApplyProductData, ApplyChannelData } from '@/api/apply-product/types';
import { LoanRateEnum } from '@/api/apply-product/types';


import { getGalileoAb } from '@/shared/galileoAb';
import { Vue, Component } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import {
  getShowRateValue,
  RateTypeNames,
} from '@/components/finance-replan/until';
import { Tag } from '@hb/coffee';

import style from '../../index.module.scss';

const EXPERIMENT_24 = {
  experimentId: '202504031044147469',
  defaultVersionID: '202504031044147548',
};

enum ABTestResult {
  CONTROL_GROUP = '1',
  EXPERIMENT_GROUP = '0'
}

const ApplyProductStore = namespace('applyProduct');

@Component
export default class RateModule extends Vue {
  @ApplyProductStore.State channel!: ApplyChannelData;

  @ApplyProductStore.State product!: ApplyProductData;

  @ApplyProductStore.State planListData!: CalcPlanListData;

  /** 标识是否展示24期实验组利率文案 */
  private needShow24ExperimentRateText = false;

  /** 这里很奇怪，风控返回的为Double类型，资产转为1.0，= . = 所以改成判断是否包含1 */
  private get isUserLoanRate24ZyUsr() {
    return this.channel?.userLoanRate24ZyUsr
        && this.channel?.userLoanRate24ZyUsr.includes(String(LoanRateEnum.LOAN_RATE_24_ZY_USR));
  }

  /** 增加2+0展示伽利略实验效果 */
  private async created() {
    if (this.isUserLoanRate24ZyUsr) {
      const { experimentId, defaultVersionID } = EXPERIMENT_24;
      const res = await getGalileoAb().getAbRes(
        experimentId,
        defaultVersionID,
      );
      this.needShow24ExperimentRateText = res.result === ABTestResult.EXPERIMENT_GROUP;
      // 如果命中实验组，曝光数据
      if (this.needShow24ExperimentRateText) {
        const { capitalCode, userLoanRate24ZyUsr, productId } = this.channel || {};
        this.$ubtModuleExposeNew('finance_loan_apply_rate_test', {
          productId,
          capitalCode,
          // 埋点平台不支持数字录入
          userLoanRateZyUsr: userLoanRate24ZyUsr,
        });
      }
    }
  }

  /**
   * 渲染实验组特殊利率展示模板
   * @param opt.rateShowText - 利率类型显示名称
   * @param opt.rateValue - 实际利率数值
   * @returns JSX元素 - 包含优惠利率标识的特殊排版内容
   */
  private renderExperimentRate(opt: {rateShowText: string, rateValue: number}) {
    const { rateShowText, rateValue } = opt;
    return (
      <div class={style.experiment24Rate}>
        {rateShowText}利率(单利)
        <span class={style.realRate}>{getShowRateValue(rateValue)}%</span>
        <span class={style.highRate}>35.9%</span>
        <div class={style.icon}>优惠利率</div>
      </div>
    );
  }

  /**
   * 主内容渲染逻辑
   * 根据渠道配置和实验分组决定展示哪种利率样式
   * @returns JSX元素 - 普通利率展示或实验组特殊展示
   */
  private renderContent() {
    const { userLoanRate24ZyUsr } = this.channel || {};
    const {
      rateType: rateDateType,
      rateValue = 0,
      couponDiscountRate,
    } = this.planListData || {}; // 合并空值处理
    const rateShowText = rateDateType ? RateTypeNames[rateDateType] : '';


    // 区分是否命中24+0 & 实验组用户 展示划线利率
    if (this.isUserLoanRate24ZyUsr && this.needShow24ExperimentRateText) {
      return this.renderExperimentRate({ rateValue, rateShowText });
    }

    if (couponDiscountRate && couponDiscountRate > 0) {
      this.$ubtModuleExposeNew('finance_loan_apply_couponDiscountRate_firstScreen', {
        couponDiscountRate,
      });
    }

    // 常规利率展示模板
    return (
        <div class={style.dueDate}>
          <div>
            {rateShowText}利率
            {this.product?.productInfo?.calcMethod
            && <span>(单利)</span>}
            <span class={style.rateValue}>{getShowRateValue(rateValue)}%</span>
            {couponDiscountRate && couponDiscountRate > 0 && (
              <div class={style.couponDiscountRateTips}>
                &nbsp;用券后
                <Tag class={style.couponDiscountRate} type="primary" highlight>
                  {getShowRateValue(couponDiscountRate, 2)}%
                </Tag>
              </div>
            )}
          </div>
        </div>
    );
  }

  private render() {
    return (this.renderContent());
  }
}
